import { format, differenceInDays, differenceInHours, isWithinInterval, parseISO } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { CalendarEvent } from '@/pages/scheduling/types';

/**
 * Checks if a shift is a sick leave based on title or is_sick_leave flag
 */
export const isSickLeave = (title?: string): boolean => {
  if (!title) return false;
  
  const sickLeaveKeywords = [
    "ΑΝΑΡΡΩΤΙΚΗ", "ΑΝΑΡΡΩΤΙΚΕΣ", "ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ", "SICK LEAVE", "SICK"
  ];
  
  const normalizedTitle = title.toUpperCase().trim();
  
  return sickLeaveKeywords.some(keyword => 
    normalizedTitle === keyword.toUpperCase() || normalizedTitle.includes(keyword.toUpperCase())
  );
};

/**
 * Checks if a shift is a regular leave based on title
 */
export const isRegularLeave = (title?: string): boolean => {
  if (!title) return false;
  
  const regularLeaveKeywords = [
    "ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ", "ΚΑΝ ΑΔΕΙΑ", "ΚΑΝ.ΑΔΕΙΑ"
  ];
  
  const normalizedTitle = title.toUpperCase().trim();
  
  return regularLeaveKeywords.some(keyword => 
    normalizedTitle === keyword.toUpperCase() || normalizedTitle.includes(keyword.toUpperCase())
  );
};

/**
 * Checks if a shift is a previous year leave based on title
 */
export const isPreviousYearLeave = (title?: string): boolean => {
  if (!title) return false;
  
  const previousYearLeaveKeywords = [
    "ΠΡΟΗΓ ΕΤΟΥΣ", "ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ", "ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ"
  ];
  
  const normalizedTitle = title.toUpperCase().trim();
  
  return previousYearLeaveKeywords.some(keyword => 
    normalizedTitle === keyword.toUpperCase() || normalizedTitle.includes(keyword.toUpperCase())
  );
};

/**
 * Checks if a shift is a holiday based on title
 */
export const isHoliday = (title?: string): boolean => {
  if (!title) return false;
  
  const holidayKeywords = [
    "ΑΡΓΙΑ", "HOLIDAY", "ΑΡΓΙΕΣ"
  ];
  
  const normalizedTitle = title.toUpperCase().trim();
  
  return holidayKeywords.some(keyword => 
    normalizedTitle === keyword.toUpperCase() || normalizedTitle.includes(keyword.toUpperCase())
  );
};

/**
 * Checks if a shift is any type of leave
 */
export const isLeave = (title?: string): boolean => {
  if (!title) return false;
  
  const leaveKeywords = [
    "ΑΔΕΙΑ", "LEAVE", "ΑΝΑΡΡΩΤΙΚΗ", "SICK", "ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ"
  ];
  
  const normalizedTitle = title.toUpperCase().trim();
  
  return leaveKeywords.some(keyword => 
    normalizedTitle === keyword.toUpperCase() || normalizedTitle.includes(keyword.toUpperCase())
  );
};

/**
 * Checks if a shift is a leave that should be excluded from overtime calculations
 * Currently, only sick leave is excluded from overtime calculations
 */
export const isLeaveForOvertimeExclusion = (title?: string): boolean => {
  return isSickLeave(title);
};

/**
 * Checks if a shift is a leave schedule and determines the type of leave
 */
export const isLeaveSchedule = (title: string) => {
  const titleLower = title.toLowerCase();
  const leaveKeywords = {
    // Greek keywords
    'αδεια': true,
    'άδεια': true,
    'αδειασ': true,
    'άδειας': true,
    'αναρρωτικη': true,
    'αναρρωτική': true,
    'κανονικη': true,
    'κανονική': true,
    'προηγ': true,
    'ρεπο': true,
    'ρεπό': true,
    'μη εργασια': true,
    'μη εργασία': true,
    
    // English keywords
    'leave': true,
    'vacation': true,
    'sick': true,
    'day off': true,
    'day-off': true,
    'regular': true,
    'annual': true,
    'holiday': true,
    'maternity': true
  };

  let leaveType = '';
  
  if (titleLower.includes('προηγ')) {
    leaveType = 'previousYear';
  } else if (titleLower.includes('αναρρωτικη') || titleLower.includes('αναρρωτική') || titleLower.includes('sick')) {
    leaveType = 'sick';
  } else if (
    titleLower.includes('αδεια') || 
    titleLower.includes('άδεια') || 
    titleLower.includes('leave') || 
    titleLower.includes('vacation') ||
    titleLower.includes('κανονικη') ||
    titleLower.includes('κανονική')
  ) {
    leaveType = 'regular';
  } else if (
    titleLower.includes('ρεπο') || 
    titleLower.includes('ρεπό') || 
    titleLower.includes('day off') || 
    titleLower.includes('day-off')
  ) {
    leaveType = 'dayOff';
  } else if (
    titleLower.includes('μη εργασια') || 
    titleLower.includes('μη εργασία') ||
    titleLower.includes('maternity')
  ) {
    leaveType = 'noWork';
  }
  
  const isLeave = Object.keys(leaveKeywords).some(keyword => 
    titleLower.includes(keyword)
  );
  
  return {
    isLeave,
    leaveType
  };
};

/**
 * Process leave schedules to generate leave data for guards
 */
export const processLeaveSchedules = (leaveSchedules: any[], guards: any[]) => {
  const leaveData: any[] = [];
  
  if (!leaveSchedules || leaveSchedules.length === 0) {
    console.log("No leave schedules to process");
    return leaveData;
  }
  
  // Create a map for faster guard lookups
  const guardsMap = new Map();
  guards.forEach(guard => {
    guardsMap.set(guard.id, guard);
  });
  
  leaveSchedules.forEach(schedule => {
    if (!schedule.assigned_guards || schedule.assigned_guards.length === 0) {
      return;
    }
    
    const { isLeave, leaveType } = isLeaveSchedule(schedule.title);
    if (!isLeave || !leaveType) {
      return;
    }
    
    const startDate = fromLocalISOString(schedule.start_date);
    const endDate = fromLocalISOString(schedule.end_date);
    
    // Calculate days (simplistic approach - just divide total hours by 8)
    const totalHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    const days = Math.round(totalHours / 8); // Assuming 8-hour workdays
    
    schedule.assigned_guards.forEach((guardId: string) => {
      if (guardsMap.has(guardId)) {
        const guard = guardsMap.get(guardId);
        
        leaveData.push({
          guardId,
          guardName: guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim(),
          type: leaveType,
          startDate,
          endDate,
          days: days > 0 ? days : 1, // Ensure at least 1 day is counted
          title: schedule.title
        });
      }
    });
  });
  
  return leaveData;
};

/**
 * Calculate leave days based on schedules
 */
export const calculateLeaveDays = (events: CalendarEvent[]) => {
  let leaveDays = 0;
  let sickDays = 0;
  let previousYearDays = 0;
  let dayOffs = 0;
  let noWorkDays = 0;
  
  events.forEach(event => {
    const { isLeave, leaveType } = isLeaveSchedule(event.title);
    
    if (isLeave) {
      const startDate = fromLocalISOString(event.start_date);
      const endDate = fromLocalISOString(event.end_date);
      
      // For single day events, count as 1
      if (format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
        if (leaveType === 'previousYear') {
          previousYearDays += 1;
        } else if (leaveType === 'sick') {
          sickDays += 1;
        } else if (leaveType === 'regular') {
          leaveDays += 1;
        } else if (leaveType === 'dayOff') {
          dayOffs += 1;
        } else if (leaveType === 'noWork') {
          noWorkDays += 1;
        }
      } else {
        // For multi-day events
        const days = differenceInDays(endDate, startDate);
        
        if (leaveType === 'previousYear') {
          previousYearDays += Math.max(1, days);
        } else if (leaveType === 'sick') {
          sickDays += Math.max(1, days);
        } else if (leaveType === 'regular') {
          leaveDays += Math.max(1, days);
        } else if (leaveType === 'dayOff') {
          dayOffs += Math.max(1, days);
        } else if (leaveType === 'noWork') {
          noWorkDays += Math.max(1, days);
        }
      }
    }
  });
  
  return {
    leaveDays,
    sickDays,
    previousYearDays,
    dayOffs,
    noWorkDays,
    total: leaveDays + sickDays + previousYearDays + dayOffs + noWorkDays
  };
};

/**
 * Helper to check if event is a working shift
 */
export const isWorkingShift = (event: CalendarEvent) => {
  const { isLeave } = isLeaveSchedule(event.title);
  return !isLeave;
};
